<template>
  <hr>
  <div class="preview-section">
    <div class="card">
      <cardHeader
        :elementType="question.el_Type"
        :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :isAgent="isAgent"
        :agentLevel="agentLevel"
        :data-q_id="selectedElement?._id"
      />
      <div class="card-body">

        <div class="mb-2">
          <h3>{{ question.el_Text }}</h3>
        </div>
        <div ref="quillPromptEditor" class="quill-prompt-viewer"></div>

        <div class="mt-3">
          <label class="form-label fw-bold">Your answer:</label>
          <div class="editor-with-palette" style="position: relative;">
            <div
              ref="quillAnswerEditor"
              class="quill-answer-editor"
              :style="{ minHeight: (question.minHeight || 200) + 'px' }"
            ></div>
            <MathPalette
              :isVisible="showMathPalette"
              @insert="insertMathSymbol"
              @close="showMathPalette = false"
            />
          </div>
        </div>

        <div class="ReportContainer" v-if="showReport">
          <div class="row">
            <div class="col">
              <h5>Results:</h5>
            </div>
            <div class="col" style="text-align: right;"><strong>Date:</strong> {{ currentDate }}</div>

            <hr>
            <div v-if="isLoading" class="loading-container">
              <img src="../../assets/loading.gif" alt="Loading..." class="loading-gif" />
              <p>Processing your answer, please wait...</p>
            </div>

            <div v-if="results.correction.length > 0">
              <h5>AI Correction :</h5>

              <div v-for="(correction, index) in results.correction" :key="index" class="card mb-3">
                <div class="card-body">
                  <h6 class="card-title"><strong>Axis:</strong> {{ correction.axis }}</h6>
                  <strong>Answer:</strong> {{ correction.response.answer }}
                  <p><strong>Response:</strong> {{ correction.response.comment }}</p>
                  <p><strong>Score Q:</strong> {{ getRealScore(correction.axis, correction.response.answer) }}</p>
                </div>
              </div>

              <hr>
              <div style="color:orange">
                <h5>Final Prompt</h5>
                <p>{{ correctionPrompt }}</p>
                <p>AI Model: {{ results.AImodel }}</p>
                <p>AI Total duration: {{ results.AItotal_duration }}</p>
              </div>
              <p><strong>Final Score:</strong> {{ totalQScore }} </p>
            </div>
            <div v-else>
              <p>A human correction is required.</p>
              <p><strong>Max Score (No AI correction) :</strong> {{ question.noAIMaxScore }} </p>
            </div>
          </div>
        </div>
      </div>
      <cardFooterLearningMode
       v-if="(publicationStore.publicationData?.mode) !== 'exam'"
        :showReport="showReport"
        :submitAnswers="showReportOnSubmit"
        :closeReport="closeReport"
        :resetSelections="resetSelections" />

    </div>
  </div>
</template>

<script setup>
import { useResponsesStore } from '../../stores/useResponsesStore.js';
import { usePublicationStore } from '@/stores/usePublicationStore'
import cardHeader from './cardHeader.vue';
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'katex/dist/katex.min.css'
import katex from 'katex'
import cardFooterLearningMode from './cardFooterLearningMode.vue';
import MathPalette from '../common/MathPalette.vue';

// Enregistrer KaTeX globalement
window.katex = katex;

const publicationStore = usePublicationStore()
const responsesStore = useResponsesStore();

const quillPromptEditor = ref(null);
const quillAnswerEditor = ref(null);
let quillPromptInstance = null;
let quillAnswerInstance = null;

defineEmits(['interaction-started'])

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  testData: {
    type: Object,
    required: true,
  },
  numOrder: [Number, null],
  questionIndex: {
    type: Number,
    required: false,
    default: 1
  },
  questionIndexMap: {
    type: Object,
    required: false
  },
  readOnly: { type: Boolean, default: false },
});

const selectedElement = computed(() => props.question);

const getQuestionNumber = () => {
  if (!props.questionIndexMap || typeof props.questionIndexMap !== 'object') {
    console.error("❌ questionIndexMap est indéfini ou n'est pas un objet.");
    return "N/A";
  }
  return props.questionIndexMap[props.question.el_ID] ?? "N/A";
};

const studentAnswers = ref('');
const showReport = ref(false);
const correctionPrompt = ref('');
const showMathPalette = ref(false);

const token = localStorage.getItem('token');
const decodedToken = decodeToken(token);
const userId = decodedToken?._id || decodedToken?.sub;
const isAgent = ref(decodedToken?.agent === true);
const agentLevel = ref(decodedToken?.agentLevel || null);

function decodeToken(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch (e) {
    console.error('Error decoding token:', e);
    return null;
  }
}

const results = ref({
  userId: userId,
  questionId: selectedElement.value.el_ID,
  type: selectedElement.value.el_Type,
  content: studentAnswers.value,
  timestamp: new Date().toISOString(),
  score: 0,
  error: 0,
  correction: [],
  AImodel: "",
  AItotal_duration: "",
  axes: selectedElement.value.correctionAxes
});

let response = {
  userId: userId,
  questionId: selectedElement.value.el_ID,
  type: selectedElement.value.el_Type,
  questionLabel: selectedElement.value.el_Text,
  timestamp: new Date().toISOString(),
  answered: false,
  score: "AI",
  axes: selectedElement.value.correctionAxes,
  maxScore: selectedElement.value.noAIMaxScore,
  hasTextReference: selectedElement.value.hasTextReference,
  refText: selectedElement.value.refText,
  corrections: []
};

const totalQScore = computed(() => {
  return results.value.correction.reduce((sum, correction) => {
    return sum + getRealScore(correction.axis, correction.response.answer);
  }, 0);
});

const currentDate = computed(() => {
  const now = new Date();
  return now.toLocaleString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
});

const closeReport = () => {
  showReport.value = false;
}

const showReportOnSubmit = () => {
  showReport.value = true;
  submitAnswers(props.question)
}

const stripHTMLTags = (input) => {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = input;
  return tempElement.textContent || tempElement.innerText || "";
}

// Configuration des toolbars selon les options
const getToolbarConfig = (toolbarType) => {
  switch (toolbarType) {
    case 'minimal':
      return [
        ['bold', 'italic'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }]
      ];
    case 'standard':
      return [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link']
      ];
    case 'full':
    default:
      return {
        container: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'align': [] }],
          ['link', 'formula', 'mathPalette'],
          ['clean']
        ],
        handlers: {
          mathPalette: function() {
            showMathPalette.value = !showMathPalette.value;
          }
        }
      };
  }
};

// Initialisation de Quill pour le prompt (read-only)
const initializePromptEditor = () => {
  if (!quillPromptEditor.value) return;

  quillPromptInstance = new Quill(quillPromptEditor.value, {
    theme: 'snow',
    readOnly: true,
    modules: {
      toolbar: false,
    },
  });

  if (props.question?.el_RichText) {
    quillPromptInstance.root.innerHTML = props.question.el_RichText;
  }
};

// Fonction pour insérer un symbole mathématique
const insertMathSymbol = (latex) => {
  if (!quillAnswerInstance) return;

  console.log('Inserting LaTeX:', latex);

  const range = quillAnswerInstance.getSelection() || { index: quillAnswerInstance.getLength() };

  // Insérer la formule LaTeX comme embed formula
  quillAnswerInstance.insertEmbed(range.index, 'formula', latex);

  // Déplacer le curseur après la formule insérée
  quillAnswerInstance.setSelection(range.index + 1);

  // Remettre le focus sur l'éditeur
  quillAnswerInstance.focus();

  // Ne plus fermer automatiquement la palette - l'étudiant la fermera
  // showMathPalette.value = false;
};

// Initialisation de Quill pour la réponse de l'étudiant
const initializeAnswerEditor = () => {
  if (!quillAnswerEditor.value) return;

  // Enregistrer l'icône personnalisée pour le bouton mathPalette
  const icons = Quill.import('ui/icons');
  icons['mathPalette'] = '🧮';

  const toolbarConfig = getToolbarConfig(props.question?.toolbarOptions || 'full');

  quillAnswerInstance = new Quill(quillAnswerEditor.value, {
    theme: 'snow',
    modules: {
      toolbar: toolbarConfig
    },
    placeholder: props.question?.placeholder || 'Write your detailed answer here...'
  });

  // Gérer le mode readOnly après l'initialisation
  if (props.readOnly) {
    quillAnswerInstance.enable(false);
  }

  // Sauvegarder les changements D'ABORD
  quillAnswerInstance.on('text-change', () => {
    studentAnswers.value = quillAnswerInstance.root.innerHTML;

    // Sauvegarder dans le store
    response.answered = quillAnswerInstance.root.innerHTML;
    response.timestamp = new Date().toISOString();
    responsesStore.saveResponse(response);
  });

  // Charger le contenu existant APRÈS avoir configuré le listener
  const savedResponse = responsesStore.getResponseForQuestion(userId, props.question.el_ID);
  if (savedResponse && savedResponse.answered) {
    quillAnswerInstance.root.innerHTML = savedResponse.answered;
    studentAnswers.value = savedResponse.answered;
  }

  // Ajouter la possibilité d'éditer les formules en cliquant dessus
  quillAnswerInstance.root.addEventListener('click', (e) => {
    const formula = e.target.closest('.ql-formula');
    if (formula) {
      const currentLatex = formula.getAttribute('data-value');
      const newLatex = prompt('Edit LaTeX formula:', currentLatex);
      if (newLatex !== null && newLatex !== currentLatex) {
        // Trouver la position de la formule dans le document
        const blot = Quill.find(formula);
        if (blot) {
          const index = quillAnswerInstance.getIndex(blot);
          quillAnswerInstance.deleteText(index, 1);
          quillAnswerInstance.insertEmbed(index, 'formula', newLatex);
          quillAnswerInstance.setSelection(index + 1);
        }
      }
    }
  });
};

onMounted(async () => {
  await nextTick();
  initializePromptEditor();
  await nextTick();
  initializeAnswerEditor();
});

// Vérifier si l'utilisateur a commencé à répondre
const handleInputChange = () => {
  if (studentAnswers.value && studentAnswers.value.trim().length > 0) {
    response.answered = studentAnswers.value;
    response.timestamp = new Date().toISOString();
    responsesStore.addOrUpdateResponse(response);
  }
};

const SequenceAxesTreatment = (data) => {
  const axes = props.question.correctionAxes;
  results.value.correction.length = 0;

  // Convertir le HTML en texte pour l'AI
  const answerText = stripHTMLTags(studentAnswers.value);

  for (let i = 0; i < axes.length; i++) {
    correctionPrompt.value =
      `you are a ${data.Subject} teacher in ${data.level},
here is the question asked to the students:
${props.question.el_Text}
Here is the student\'s answer:
${answerText}
Can you say if ${axes[i].axis} ?
Format your answer in the strict form of a JSON and assign a score out of 10 that strictly concerns this aspect of the question.
Here is the structure of the JSON to respect { "answer": false/true, "score": 0-10, "comment": "here is your comment" }.
Be careful, no information should accompany the JSON!`;

    askLMStudio(correctionPrompt.value.trim(), axes[i].axis);
  }
}

const submitAnswers = (question) => {
  const data = props.testData;

  if (!question) return;

  // Vérifier si une réponse a été saisie
  const textContent = stripHTMLTags(studentAnswers.value);
  if (!textContent || textContent.trim().length === 0) {
    alert("There is no answer to submit. Try again!");
    return;
  }

  if (question.AIisActivated) {
    console.log("AI IS ACTIVATED")
    SequenceAxesTreatment(data)
  } else {
    console.log("AI IS NOT ACTIVATED")
  }
}

const isLoading = ref(false);

function getRealScore(axis, answer) {
  let QScore = 0;
  let correctionAxes = selectedElement.value.correctionAxes;

  for (let i = 0; i < correctionAxes.length; i++) {
    if (axis === correctionAxes[i].axis) {
      return answer ? correctionAxes[i].point : (-correctionAxes[i].penalty || 0);
    }
  }

  results.score = results.score + QScore;
  return QScore;
}

const askLMStudio = (Question, currentAxis, attempt = 1, maxRetries = 3) => {
  isLoading.value = true;
  const API_URL = "http://localhost:1234/v1/completions";

  fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      model: "deepseek-r1-distill-qwen-7b",
      prompt: Question,
      temperature: 0.3,
      max_tokens: 1000,
      stop: null
    })
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log(`Tentative ${attempt} - Réponse de l'API :`, data);
      if (isJSON(data.choices[0].text)) {
        let parsedResponse = JSON.parse(data.choices[0].text);
        results.value.correction.push({
          axis: currentAxis,
          response: parsedResponse,
          score: parsedResponse.score,
        });
        results.value.AImodel = data.model;
        results.value.AItotal_duration = data.total_duration;
      } else {
        let parsedResponse = extractJsonFromResponse(data.choices[0].text);
        if (parsedResponse) {
          results.value.correction.push({
            axis: currentAxis,
            response: parsedResponse,
            score: parsedResponse.score
          });
          results.value.AImodel = data.model;
          results.value.AItotal_duration = data.total_duration;
        } else {
          console.error(`Tentative ${attempt}: Impossible d'extraire une réponse JSON valide.`);
          if (attempt < maxRetries) {
            console.warn(`Relance de l'API... Tentative ${attempt + 1}`);
            askLMStudio(Question, currentAxis, attempt + 1, maxRetries);
          } else {
            console.error("Échec après plusieurs tentatives.");
          }
        }
      }
      isLoading.value = false;
    })
    .catch(error => {
      console.error("Erreur :", error);
      isLoading.value = false;
    });
};

function isJSON(str) {
  if (typeof str !== "string") {
    return false;
  }
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null;
  } catch (e) {
    return false;
  }
}

function extractJsonFromResponse(responseText) {
  const jsonMatch = responseText.match(/```json\n([\s\S]*?)\n```/);
  if (jsonMatch && jsonMatch[1]) {
    try {
      return JSON.parse(jsonMatch[1]);
    } catch (error) {
      console.error("Erreur de parsing JSON :", error);
      return null;
    }
  }
  console.warn("Aucun JSON trouvé dans la réponse AI.");
  return null;
}

const resetSelections = () => {
  const confirmed = window.confirm('Êtes-vous sûr de vouloir réinitialiser vos réponses ?');
  if (confirmed) {
    if (quillAnswerInstance) {
      quillAnswerInstance.setText('');
    }
    studentAnswers.value = '';
    results.value.correction = [];
    showReport.value = false;

    // Supprimer du store
    responsesStore.removeResponse(userId, props.question.el_ID);
  }
};

onBeforeUnmount(() => {
  if (quillPromptInstance) {
    quillPromptInstance = null;
  }
  if (quillAnswerInstance) {
    quillAnswerInstance = null;
  }
});
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
}

.loading-gif {
  width: 50px;
  height: 50px;
  margin-bottom: 10px;
}

.ReportContainer {
  border: grey 1px solid;
  border-radius: 3px;
  padding: 20px;
  background-color: rgb(54, 63, 67);
  color: greenyellow;
  margin-top: 20px;
}

.card-body {
  border: 1px solid gainsboro !important
}

.quill-prompt-viewer {
  min-height: max-content !important;
  height: auto !important;
  border: 0 !important;
  overflow: hidden !important;
  margin-bottom: 15px;
}

.quill-prompt-viewer :deep(.ql-container.ql-snow) {
  border: none !important;
}

.quill-answer-editor {
  background: white;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.quill-answer-editor :deep(.ql-container) {
  min-height: 200px;
}

.quill-answer-editor :deep(.ql-editor) {
  min-height: inherit;
}

.quill-answer-editor :deep(.ql-editor > *) {
  cursor: text !important;
}

.quill-answer-editor.readOnly {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
</style>
